/**
 * Page-specific help dialogs
 *
 * Provides contextual help for non-admin pages. Help content is keyed
 * by URL pathname and rendered in a non-modal jQuery UI dialog so users
 * can keep it open as a reference while working.
 */

// jQuery UI dialog types (already declared in dialogs.ts)

/** Help entry for a single page */
interface HelpEntry {
  title: string;
  body: string;
}

/** Map URL pathname → help content for pages that have help */
const helpContent: Record<string, HelpEntry> = {
  '/vms': {
    title: 'Virtual Machines',
    body: `
      <div class="help-content">
        <h4>Overview</h4>
        <p>This page shows all virtual machines you have access to. Each row in the
        table represents one virtual machine (VM) — a computer running inside the
        server.</p>

        <h4>Starting and Stopping</h4>
        <p>Use the <strong>Start</strong> button to power on a VM and
        <strong>Stop</strong> to gracefully shut it down (like clicking
        "Shut down" in Windows). <strong>Force Power Off</strong> is an
        emergency option — equivalent to pulling the power cord — and should
        only be used if the VM is unresponsive.</p>

        <h4>Connecting via Remote Desktop (RDP)</h4>
        <p>The primary way to use a VM is through <strong>Remote Desktop
        (RDP)</strong>. Click the <strong>Connect (RDP)</strong> button to
        download a connection file that opens in:</p>
        <ul>
          <li><strong>Windows</strong> — built-in Remote Desktop Connection
          (search "Remote Desktop" in the Start menu)</li>
          <li><strong>Mac</strong> — <em>Microsoft Remote Desktop</em> app
          (free on the App Store)</li>
          <li><strong>iPhone / iPad</strong> — <em>Microsoft Remote Desktop</em>
          app (free on the App Store)</li>
        </ul>

        <h4>Screen View (VNC Guest)</h4>
        <p><strong>Screen View (VNC Guest)</strong> in the Connect menu opens a
        read-only browser-based view of the VM's desktop. This is useful when
        an operator needs to see what is currently on a user's screen while
        they are connected via Remote Desktop (for example, to assist with a
        problem). Input is disabled by default — click <strong>Allow Control</strong>
        in the toolbar to enable keyboard and mouse input when you need to
        interact with the guest.</p>

        <h4>Console (VNC)</h4>
        <p><strong>Console (VNC)</strong> shows the VM's raw display output —
        like looking at a physical monitor plugged into the machine. This is
        mainly for troubleshooting situations where the VM has lost network
        connectivity, is stuck at a boot screen, or is otherwise unreachable
        via Remote Desktop or Screen View.</p>

        <h4>Table Columns</h4>
        <p>The table shows key information about each VM. You can customize which
        columns are visible by clicking <strong>Settings</strong> in the top-right
        corner and adjusting the <em>VM Table Columns</em> section.</p>
      </div>
    `,
  },

  '/users': {
    title: 'User Management',
    body: `
      <div class="help-content">
        <h4>Overview</h4>
        <p>This page lets you manage user accounts — the people who can log in to
        VMSMan.IO and access virtual machines.</p>

        <h4>Creating Users</h4>
        <p>Click <strong>Create User</strong> to add a new account. You will need
        to provide a username and email address. The user will receive an
        <strong>invitation email</strong> with a link to set their password.
        Alternatively, you can set a password manually.</p>

        <h4>Access Levels (Roles)</h4>
        <p>Each user has an access level that controls what they can do:</p>
        <ul>
          <li><strong>User</strong> — Can log in and use the VMs assigned to them.
          This is the right choice for most employees.</li>
          <li><strong>Operator</strong> — Can do everything a User can, plus manage
          other User accounts and assign VM access. Good for team leads or IT
          staff.</li>
          <li><strong>Admin</strong> — Full access to all settings, VMs, networks,
          and system configuration.</li>
        </ul>

        <h4>Assigning VM Access</h4>
        <p>When creating or editing a user, you can select which VMs they are
        allowed to access. Only assigned VMs will appear on their Virtual Machines
        page. Admins always have access to all VMs.</p>

        <h4>Password Resets</h4>
        <p>If a user forgets their password, open their account and click
        <strong>Reset Password</strong>. This sends them a reset link by email,
        or you can set a new password manually.</p>
      </div>
    `,
  },
};

/**
 * Look up help content for the current page.
 * Returns the help entry or null if the current page has no help.
 */
export function getHelpForCurrentPage(): HelpEntry | null {
  return helpContent[window.location.pathname] ?? null;
}

/** Track the active help dialog so we don't open duplicates */
let activeHelpDialog: JQuery | null = null;

/**
 * Open (or focus) the help dialog for the current page.
 */
export function showHelpDialog(): void {
  const help = getHelpForCurrentPage();
  if (!help) return;

  // If already open, just focus it
  if (activeHelpDialog) {
    try {
      activeHelpDialog.dialog('open');
    } catch {
      activeHelpDialog = null;
    }
    if (activeHelpDialog) return;
  }

  activeHelpDialog = $('<div></div>')
    .appendTo('body')
    .html(help.body)
    .dialog({
      modal: false,
      title: `Help: ${help.title}`,
      width: 550,
      autoOpen: true,
      resizable: true,
      buttons: {
        Close: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
        activeHelpDialog = null;
      },
    });
}

// Expose to window for onclick handlers in topbar
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).showHelpDialog = showHelpDialog;
}
